import styled from 'styled-components';
import { margin, variant } from 'styled-system';
import {
  BorderProps,
  ColorProps,
  LayoutProps,
  MarginProps,
  PaddingProps,
  PropsWithPseudo,
  TypographyProps,
} from '../../../theme/styledProps';

export type LozengeVariant = 'default' | 'dot';
export type LozengeSize = 'small' | 'large';
export type LozengeAlt = 'default' | 'alt';
export type LozengeColor = 'primary' | 'success' | 'warning' | 'error' | 'new' | 'default';

type Pseudo = '&::before' | '&::after';

const dotColor: Record<LozengeColor, string[]> = {
  primary: ['primary.400', 'primary.100'],
  success: ['success.400', 'success.200'],
  warning: ['warning.400', 'warning.500'],
  error: ['error.400', 'error.100'],
  new: ['indigo.400', 'indigo.100'],
  default: ['neutral.400', 'neutral.400'],
};

const iconColor: Record<LozengeColor, string[]> = {
  primary: ['primary.700', 'bg'],
  success: ['success.700', 'bg'],
  warning: ['neutral.700', 'neutral.800'],
  error: ['error.700', 'bg'],
  new: ['indigo.700', 'bg'],
  default: ['neutral.700', 'bg'],
};

interface LozengeProps extends MarginProps {
  variant?: LozengeVariant;
  variantColor?: LozengeColor;
  size?: LozengeSize;
  isAlt?: boolean;
  icon?: string;
}

export const StyledLozenge = styled.div<LozengeProps>(
  ({ theme }) => ({
    fontFamily: theme.fontFamily,
    borderRadius: theme.radii.medium,
    fontWeight: theme.fontWeights['600'],
    position: 'relative',
    textAlign: 'center',
    display: 'inline-flex',
    appearance: 'none',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  }),
  ({ variantColor = 'default', isAlt }) =>
    variant<
      PropsWithPseudo<
        ColorProps & LayoutProps & BorderProps & MarginProps & { content?: string },
        Pseudo
      >,
      LozengeVariant
    >({
      variants: {
        default: {},
        dot: {
          '&::before': {
            content: '""',
            width: 6,
            height: 6,
            borderRadius: 'full',
            bg: isAlt ? dotColor[variantColor][1] : dotColor[variantColor][0],
            mr: '4',
          },
        },
      },
    }),

  ({ isAlt }) =>
    variant<ColorProps, LozengeColor>({
      prop: 'variantColor',
      variants: {
        primary: {
          bg: isAlt ? 'primary.500' : 'primary.100',
          color: isAlt ? 'bg' : 'primary.700',
        },
        success: {
          bg: isAlt ? 'success.600' : 'success.100',
          color: isAlt ? 'bg' : 'success.700',
        },
        warning: {
          bg: isAlt ? 'warning.300' : 'warning.100',
          color: isAlt ? 'neutral.800' : 'neutral.700',
        },
        error: {
          bg: isAlt ? 'error.600' : 'error.100',
          color: isAlt ? 'bg' : 'error.700',
        },
        new: {
          bg: isAlt ? 'indigo.600' : 'indigo.100',
          color: isAlt ? 'bg' : 'indigo.700',
        },
        default: {
          bg: isAlt ? 'neutral.600' : 'neutral.200',
          color: isAlt ? 'bg' : 'neutral.700',
        },
      },
    }),

  variant<PaddingProps & TypographyProps, LozengeSize>({
    prop: 'size',
    variants: {
      small: {
        px: '8',
        py: '2',
        fontSize: 'small',
        lineHeight: 'small',
      },
      large: {
        px: '8',
        py: '2',
        fontSize: 'body',
        lineHeight: 'body',
      },
    },
  }),
  margin,
);

interface LozengeIconProps extends MarginProps {
  variantColor?: LozengeColor;
  isAlt?: boolean;
  size?: LozengeSize;
}

export const LozengeIcon = styled.i<LozengeIconProps>(
  ({ variantColor = 'default', isAlt }) => ({
    color: isAlt ? iconColor[variantColor][1] : iconColor[variantColor][0],
  }),
  variant<PaddingProps & TypographyProps, LozengeSize>({
    prop: 'size',
    variants: {
      small: {
        fontSize: 'small',
        lineHeight: 'small',
      },
      large: {
        fontSize: 'body',
        lineHeight: 'body',
      },
    },
  }),
  margin,
);
